import dayjs from 'dayjs';
import { Lease } from '@urbanmix-tech/shared-js';

// Debug logging for lease import - outputs to console and collects logs
const DEBUG_LEASE_IMPORT = true;
const debugLogs: string[] = [];

function debugLog(message: string, data?: unknown) {
  if (!DEBUG_LEASE_IMPORT) return;
  const timestamp = new Date().toISOString();
  const logMessage = data
    ? `[${timestamp}] ${message}: ${JSON.stringify(data, null, 2)}`
    : `[${timestamp}] ${message}`;
  debugLogs.push(logMessage);
  console.log('[LeaseImport]', logMessage);
}

export function getDebugLogs(): string[] {
  return [...debugLogs];
}

export function clearDebugLogs(): void {
  debugLogs.length = 0;
}

export function downloadDebugLogs(): void {
  const content = debugLogs.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `lease-import-debug-${new Date().toISOString()}.log`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Parse currency string to number
 * Handles formats like "2,500.00", "$2,500", "10,000,000.00"
 */
export function parseCurrency(value: string): number {
  if (!value) return 0;
  return parseFloat(value.replace(/[^0-9.-]/g, ''));
}

/**
 * Parse date string in MM/DD/YYYY format
 */
export function parseDate(value: string): Date | null {
  if (!value) return null;
  return dayjs(value, 'MM/DD/YYYY').toDate();
}

/**
 * Convert any date-like value to a Date object
 * Handles: Date, Firestore Timestamp, dayjs-parseable values, ISO 8601 strings
 */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  // Handle Firestore Timestamp (has toDate method)
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  // Handle Date objects
  if (value instanceof Date) {
    return value;
  }
  // Handle ISO 8601 string dates (YYYY-MM-DD)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return dayjs(value, 'YYYY-MM-DD').toDate();
  }
  // Handle other parseable values via dayjs
  const parsed = dayjs(value as string | number);
  return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Compare two leases at day-level precision
 * Ignores time components and allows floating-point tolerance
 */
export function leaseMatchesImportData(
  lease: Lease,
  importData: Partial<Lease>
): boolean {
  const unitId = importData.unitId || lease.unitId;
  debugLog(`leaseMatchesImportData - Unit ${unitId} - Raw values`, {
    existingLeaseStartDate: lease.leaseStartDate,
    existingLeaseStartDateType: typeof lease.leaseStartDate,
    existingLeaseStartDateConstructor: lease.leaseStartDate?.constructor?.name,
    existingLeaseEndDate: lease.leaseEndDate,
    importStartDate: importData.leaseStartDate,
    importEndDate: importData.leaseEndDate,
    existingRent: lease.monthlyRent,
    importRent: importData.monthlyRent,
    existingDeposit: lease.securityDeposit,
    importDeposit: importData.securityDeposit,
  });

  // Convert dates (handles Firestore Timestamps)
  const leaseStart = toDate(lease.leaseStartDate);
  const leaseEnd = toDate(lease.leaseEndDate);
  const importStart = toDate(importData.leaseStartDate);
  const importEnd = toDate(importData.leaseEndDate);

  debugLog(`leaseMatchesImportData - Unit ${unitId} - Converted dates`, {
    leaseStart: leaseStart?.toISOString(),
    leaseEnd: leaseEnd?.toISOString(),
    importStart: importStart?.toISOString(),
    importEnd: importEnd?.toISOString(),
  });

  // Compare start dates at day level
  const startMatch =
    (leaseStart && importStart && dayjs(leaseStart).isSame(dayjs(importStart), 'day')) ||
    (!importStart && !leaseStart);

  // Compare end dates at day level
  const endMatch =
    (leaseEnd && importEnd && dayjs(leaseEnd).isSame(dayjs(importEnd), 'day')) ||
    (!importEnd && !leaseEnd);

  // Compare rent with floating-point tolerance (< 0.01)
  const rentMatch =
    Math.abs(lease.monthlyRent - (importData.monthlyRent || 0)) < 0.01 ||
    (!importData.monthlyRent && !lease.monthlyRent);

  // Compare security deposit (optional field)
  const depositMatch =
    !importData.securityDeposit ||
    Math.abs((lease.securityDeposit || 0) - importData.securityDeposit) < 0.01;

  debugLog(`leaseMatchesImportData - Unit ${unitId} - Match results`, {
    startMatch,
    endMatch,
    rentMatch,
    depositMatch,
    overallMatch: startMatch && endMatch && rentMatch && depositMatch,
  });

  return startMatch && endMatch && rentMatch && depositMatch;
}

/**
 * Validate lease data has required fields and valid values
 */
export function validateLeaseData(
  leaseData: Partial<Lease>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!leaseData.unitId) {
    errors.push('unitId is required');
  }

  if (!leaseData.leaseStartDate) {
    errors.push('leaseStartDate is required');
  }

  if (!leaseData.leaseEndDate) {
    errors.push('leaseEndDate is required');
  }

  if (
    leaseData.monthlyRent !== undefined &&
    leaseData.monthlyRent !== null &&
    leaseData.monthlyRent < 0
  ) {
    errors.push('monthlyRent cannot be negative');
  }

  if (
    leaseData.leaseStartDate &&
    leaseData.leaseEndDate &&
    dayjs(leaseData.leaseStartDate).isAfter(dayjs(leaseData.leaseEndDate))
  ) {
    errors.push('leaseStartDate cannot be after leaseEndDate');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Calculate lease length in months
 */
export function calculateLeaseLength(
  startDate: Date,
  endDate: Date
): number {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const exactMonths = end.diff(start, 'month', true);

  // Round to nearest month if within ~3 days (0.1 of a month ≈ 3 days)
  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return roundedMonths;
  }

  return exactMonths;
}

/**
 * Calculate concession from difference between monthly rent and effective rent
 * If monthlyRent > effectiveRent, there is a concession applied
 * The effectiveRent is the net rent the tenant actually pays after concession
 *
 * NOTE: This function is kept for potential future use but is NOT currently used
 * in the import phase. The calculation based on rent difference was found to be
 * inaccurate for determining concessions during import.
 *
 * @param monthlyRent - The monthly rent amount (higher)
 * @param effectiveRent - The effective/net rent after concession (lower)
 * @param leaseStartDate - Lease start date for calculating term
 * @param leaseEndDate - Lease end date for calculating term
 * @returns CalculatedConcession or null if no concession
 */
export function calculateConcessionFromEffectiveRent(
  monthlyRent: number | undefined,
  effectiveRent: number | undefined,
  leaseStartDate: unknown,
  leaseEndDate: unknown
): CalculatedConcession | null {
  // If effectiveRent is not provided or equals monthlyRent, no concession
  if (!effectiveRent || !monthlyRent || effectiveRent >= monthlyRent) {
    return null;
  }

  const startDate = toDate(leaseStartDate);
  const endDate = toDate(leaseEndDate);

  if (!startDate || !endDate) {
    return null;
  }

  // Calculate lease length in months
  const leaseMonths = calculateLeaseLength(startDate, endDate);
  if (leaseMonths <= 0) {
    return null;
  }

  // Monthly difference (how much less they pay per month)
  const monthlyDifference = monthlyRent - effectiveRent;

  // Total concession over the lease term
  const totalConcessionAmount = Math.round(monthlyDifference * leaseMonths * 100) / 100;

  // Calculate months of free rent equivalent (at monthly rent rate)
  const monthsOfConcession = Math.round((totalConcessionAmount / monthlyRent) * 100) / 100;

  debugLog('Calculated concession from rent difference', {
    monthlyRent,
    effectiveRent,
    monthlyDifference,
    leaseMonths,
    totalConcessionAmount,
    monthsOfConcession,
  });

  return {
    totalConcessionAmount,
    monthsOfConcession,
    monthlyDifference,
    monthlyRent,
    effectiveRent,
  };
}

/**
 * Compare two leases and return differences
 */
export function compareLeases(
  lease1: Partial<Lease>,
  lease2: Partial<Lease>
): { identical: boolean; differences: string[] } {
  const differences: string[] = [];

  // Convert dates (handles Firestore Timestamps)
  const lease1Start = toDate(lease1.leaseStartDate);
  const lease1End = toDate(lease1.leaseEndDate);
  const lease2Start = toDate(lease2.leaseStartDate);
  const lease2End = toDate(lease2.leaseEndDate);

  if (
    lease1.unitId === lease2.unitId ||
    (!lease1.unitId && !lease2.unitId)
  ) {
    // unitIds match
  } else {
    differences.push('unitId differs');
  }

  if (
    dayjs(lease1Start).isSame(dayjs(lease2Start), 'day') ||
    (!lease1Start && !lease2Start)
  ) {
    // dates match
  } else {
    differences.push('leaseStartDate differs');
  }

  if (
    dayjs(lease1End).isSame(dayjs(lease2End), 'day') ||
    (!lease1End && !lease2End)
  ) {
    // dates match
  } else {
    differences.push('leaseEndDate differs');
  }

  if (
    Math.abs((lease1.monthlyRent || 0) - (lease2.monthlyRent || 0)) < 0.01 ||
    (!lease1.monthlyRent && !lease2.monthlyRent)
  ) {
    // rents match
  } else {
    differences.push('monthlyRent differs');
  }

  if (
    Math.abs(
      (lease1.securityDeposit || 0) - (lease2.securityDeposit || 0)
    ) < 0.01 ||
    (!lease1.securityDeposit && !lease2.securityDeposit)
  ) {
    // deposits match
  } else {
    differences.push('securityDeposit differs');
  }

  return {
    identical: differences.length === 0,
    differences,
  };
}

/**
 * Options for resolving conflicts when leases overlap
 */
export type ConflictResolution =
  | 'cancel_existing_create_new'  // Cancel existing lease and create new one
  | 'overwrite_existing'          // Update existing lease with new data
  | 'skip'                        // Do nothing, keep existing lease
  | 'renewal'                     // Create new lease and link as renewal
  | 'past_lease'                  // Add as a past/historical lease
  | 'end_existing'                // End existing lease (set end date to import date)
  | 'cancel_existing'             // Cancel existing lease (was never executed)
  | 'skip_and_check';             // Skip import but create action item to verify current lease

/**
 * Calculated concession info from monthly rent vs effective rent difference
 */
export type CalculatedConcession = {
  totalConcessionAmount: number;  // Total dollar amount of concession over lease term
  monthsOfConcession: number;     // Number of months equivalent (at full rent)
  monthlyDifference: number;      // Difference per month (monthlyRent - effectiveRent)
  monthlyRent: number;            // Monthly rent amount (higher amount)
  effectiveRent: number;          // Effective/net rent after concession (lower amount)
};

/**
 * Represents a lease that will be created during import
 */
export type LeaseCreationPreview = {
  unitId: string;
  importData: Partial<Lease>;
  existingLease: Lease | null;
  action: 'create' | 'update' | 'skip';
  reason: string;
  details: LeaseCreationDetails;
  hasConflict: boolean;
  conflictType?: 'dates_overlap' | 'dates_differ' | 'renewal' | 'past_lease' | 'early_start_date' | 'vacant_conflict';
  selectedResolution?: ConflictResolution;
  isPastLease?: boolean;
};

/**
 * Details about what's changing in a lease
 */
export type LeaseCreationDetails = {
  startDateChanged?: { from: Date | null; to: Date | null };
  endDateChanged?: { from: Date | null; to: Date | null };
  rentChanged?: { from: number; to: number; percentChange: number };
  depositChanged?: { from: number; to: number };
};

/**
 * Check if two date ranges overlap
 */
export function datesOverlap(
  start1: Date | null,
  end1: Date | null,
  start2: Date | null,
  end2: Date | null
): boolean {
  if (!start1 || !end1 || !start2 || !end2) return false;

  const s1 = dayjs(start1);
  const e1 = dayjs(end1);
  const s2 = dayjs(start2);
  const e2 = dayjs(end2);

  // Ranges overlap if one starts before the other ends
  return s1.isBefore(e2) && s2.isBefore(e1);
}

/**
 * Check if a new lease is likely a renewal of an existing lease
 * A renewal is detected when the new lease start date is within ±1 day of the existing lease end date
 */
export function isLeaseRenewal(
  existingEndDate: unknown,
  newStartDate: unknown
): boolean {
  // Convert to Date objects (handles Firestore Timestamps)
  const endDate = toDate(existingEndDate);
  const startDate = toDate(newStartDate);

  if (!endDate || !startDate) return false;

  const endDay = dayjs(endDate);
  const startDay = dayjs(startDate);

  // Check if new lease starts within 1 day of existing lease ending
  const daysDiff = startDay.diff(endDay, 'day');
  return daysDiff >= -1 && daysDiff <= 1;
}

/**
 * Check if a lease is a past lease (end date is in the past)
 */
export function isPastLease(leaseEndDate: unknown): boolean {
  const endDate = toDate(leaseEndDate);
  if (!endDate) return false;

  const endDay = dayjs(endDate);
  const today = dayjs().startOf('day');

  // Lease is past if it ended before today
  return endDay.isBefore(today);
}

/**
 * Analyze a lease import and determine what action should be taken
 */
export function analyzeLeaseImport(
  importData: Partial<Lease>,
  existingLease: Lease | null
): LeaseCreationPreview {
  const unitId = importData.unitId as string;

  // Case 0: Vacant import — no lease data in CSV row
  const isVacantImport = !importData.leaseStartDate && !importData.monthlyRent;
  if (isVacantImport) {
    if (!existingLease) {
      return {
        unitId,
        importData,
        existingLease: null,
        action: 'skip',
        reason: 'Vacant unit — no lease to import',
        details: {},
        hasConflict: false,
      };
    }

    const existingStartDate = toDate(existingLease.leaseStartDate);
    const existingEndDate = toDate(existingLease.leaseEndDate);
    const isExpired = existingEndDate && !dayjs(existingEndDate).isAfter(dayjs(), 'day');
    const isFuture = existingStartDate && dayjs(existingStartDate).isAfter(dayjs(), 'day');

    if (isExpired || isFuture) {
      return {
        unitId,
        importData,
        existingLease,
        action: 'skip',
        reason: isFuture
          ? 'Unit vacant now, future lease exists'
          : 'Lease expired, unit now vacant',
        details: {},
        hasConflict: false,
      };
    }

    // Active lease but CSV says vacant — conflict
    return {
      unitId,
      importData,
      existingLease,
      action: 'update',
      reason: 'Imported unit is vacant but has an active lease',
      details: {
        rentChanged: {
          from: existingLease.monthlyRent || 0,
          to: 0,
          percentChange: -100,
        },
      },
      hasConflict: true,
      conflictType: 'vacant_conflict',
      selectedResolution: 'skip',
    };
  }

  // Case 1: No existing lease - create new or add as past lease
  if (!existingLease) {
    const isPast = isPastLease(importData.leaseEndDate);

    if (isPast) {
      return {
        unitId,
        importData,
        existingLease: null,
        action: 'create',
        reason: 'Past lease - lease dates are in the past',
        details: {},
        hasConflict: false,
        conflictType: 'past_lease',
        selectedResolution: 'past_lease', // Default to create as past lease
        isPastLease: true,
      };
    }

    return {
      unitId,
      importData,
      existingLease: null,
      action: 'create',
      reason: 'New lease - no existing lease for this unit',
      details: {},
      hasConflict: false,
    };
  }

  // Case 2: Check if lease matches
  if (leaseMatchesImportData(existingLease, importData)) {
    return {
      unitId,
      importData,
      existingLease,
      action: 'skip',
      reason: 'Lease data matches existing lease',
      details: {},
      hasConflict: false,
    };
  }

  // Case 3: Lease differs - figure out what changed
  const details: LeaseCreationDetails = {};
  const changes: string[] = [];

  // Convert dates first (handles Firestore Timestamps)
  const existingStartDate = toDate(existingLease.leaseStartDate);
  const existingEndDate = toDate(existingLease.leaseEndDate);
  const importStartDate = toDate(importData.leaseStartDate);
  const importEndDate = toDate(importData.leaseEndDate);

  debugLog(`analyzeLeaseImport - Unit ${unitId} - Detecting changes`, {
    existingStartDate: existingStartDate?.toISOString(),
    existingEndDate: existingEndDate?.toISOString(),
    importStartDate: importStartDate?.toISOString(),
    importEndDate: importEndDate?.toISOString(),
    existingRent: existingLease.monthlyRent,
    importRent: importData.monthlyRent,
  });

  // Check start date
  const startDateDiffers =
    importStartDate &&
    !dayjs(existingStartDate).isSame(dayjs(importStartDate), 'day');
  if (startDateDiffers) {
    details.startDateChanged = {
      from: existingStartDate,
      to: importStartDate,
    };
    changes.push('start date');
  }

  // Check end date
  const endDateDiffers =
    importEndDate &&
    !dayjs(existingEndDate).isSame(dayjs(importEndDate), 'day');
  if (endDateDiffers) {
    details.endDateChanged = {
      from: existingEndDate,
      to: importEndDate,
    };
    changes.push('end date');
  }

  // Check rent
  const existingRent = existingLease.monthlyRent || 0;
  const importRent = importData.monthlyRent || 0;
  if (Math.abs(existingRent - importRent) >= 0.01) {
    const percentChange =
      existingRent > 0
        ? ((importRent - existingRent) / existingRent) * 100
        : 100;
    details.rentChanged = {
      from: existingRent,
      to: importRent,
      percentChange,
    };
    changes.push('rent');
  }

  // Check deposit
  const existingDeposit = existingLease.securityDeposit || 0;
  const importDeposit = importData.securityDeposit || 0;
  if (Math.abs(existingDeposit - importDeposit) >= 0.01) {
    details.depositChanged = {
      from: existingDeposit,
      to: importDeposit,
    };
    changes.push('security deposit');
  }

  const reason =
    changes.length === 1
      ? `${changes[0].charAt(0).toUpperCase() + changes[0].slice(1)} changed`
      : `Multiple changes: ${changes.join(', ')}`;

  // Check if dates overlap
  const hasDateOverlap = datesOverlap(
    existingStartDate,
    existingEndDate,
    importStartDate,
    importEndDate
  );

  // Conflict detection:
  // - Only mark as conflict if dates overlap (competing lease periods)
  // - If dates don't overlap, it's a new separate lease (renewal, past lease, or future lease)
  const hasConflict = hasDateOverlap;
  let conflictType: 'dates_overlap' | 'dates_differ' | 'renewal' | 'past_lease' | 'early_start_date' | undefined = undefined;
  let selectedResolution: ConflictResolution | undefined = undefined;
  let action: 'create' | 'update' | 'skip' = 'update';

  // Check if only start date differs and it's more than 1 day earlier
  const onlyStartDateDiffers = startDateDiffers && !endDateDiffers && !details.rentChanged && !details.depositChanged;
  const startDateDaysDiff = existingStartDate && importStartDate
    ? dayjs(existingStartDate).diff(dayjs(importStartDate), 'day')
    : 0;
  const isEarlyStartDate = onlyStartDateDiffers && startDateDaysDiff > 1;

  if (isEarlyStartDate) {
    // Early start date - import has earlier start, skip by default
    conflictType = 'early_start_date';
    action = 'skip';
    debugLog(`Early start date for unit ${unitId}`, {
      existingStart: existingStartDate?.toISOString(),
      importStart: importStartDate?.toISOString(),
      daysDiff: startDateDaysDiff,
    });

    return {
      unitId,
      importData,
      existingLease,
      action: 'skip',
      reason: `Start date is ${startDateDaysDiff} days earlier than existing lease`,
      details,
      hasConflict: false,
      conflictType: 'early_start_date',
    };
  } else if (startDateDiffers || endDateDiffers) {
    // Dates changed - determine the type of change
    if (hasDateOverlap) {
      // Overlapping dates = conflict, user must choose
      conflictType = 'dates_overlap';
      selectedResolution = 'skip'; // Default to skip for conflicts
      debugLog(`Date overlap conflict for unit ${unitId}`, {
        existingStart: existingStartDate?.toISOString(),
        existingEnd: existingEndDate?.toISOString(),
        importStart: importStartDate?.toISOString(),
        importEnd: importEndDate?.toISOString(),
      });
    } else {
      // No overlap - check if it's a renewal or separate lease
      const isRenewal = isLeaseRenewal(existingEndDate, importStartDate);

      if (isRenewal) {
        // Sequential dates = renewal, create new lease
        conflictType = 'renewal';
        action = 'create';
        debugLog(`Lease renewal detected for unit ${unitId}`, {
          existingEndDate: existingEndDate?.toISOString(),
          newStartDate: importStartDate?.toISOString(),
        });
      } else {
        // Completely separate dates - create new lease
        action = 'create';
        debugLog(`New separate lease period for unit ${unitId}`, {
          existingStart: existingStartDate?.toISOString(),
          existingEnd: existingEndDate?.toISOString(),
          importStart: importStartDate?.toISOString(),
          importEnd: importEndDate?.toISOString(),
        });
      }
    }
  } else if (changes.length > 0) {
    // Only rent/deposit changed, same dates - update existing
    action = 'update';
  }

  return {
    unitId,
    importData,
    existingLease,
    action,
    reason,
    details,
    hasConflict,
    conflictType,
    selectedResolution,
  };
}

/**
 * Generate preview data for all lease imports
 */
export function generateLeaseImportPreview(
  leaseUpdates: Partial<Lease>[],
  existingLeases: Lease[]
): {
  toCreate: LeaseCreationPreview[];
  toSkip: LeaseCreationPreview[];
  summary: {
    totalImported: number;
    newLeases: number;
    updatedLeases: number;
    skipped: number;
  };
} {
  // Clear previous debug logs for fresh run
  clearDebugLogs();

  debugLog('generateLeaseImportPreview - Starting', {
    leaseUpdatesCount: leaseUpdates.length,
    existingLeasesCount: existingLeases.length,
  });

  // Log existing leases info
  existingLeases.forEach((lease) => {
    // debugLog(`Existing lease - Unit ${lease.unitId}`, {
    //   id: lease.id,
    //   startDate: lease.leaseStartDate,
    //   startDateType: typeof lease.leaseStartDate,
    //   startDateConstructor: lease.leaseStartDate?.constructor?.name,
    //   endDate: lease.leaseEndDate,
    //   monthlyRent: lease.monthlyRent,
    //   securityDeposit: lease.securityDeposit,
    // });
  });

  const toCreate: LeaseCreationPreview[] = [];
  const toSkip: LeaseCreationPreview[] = [];

  for (const leaseData of leaseUpdates) {
    if (!leaseData.unitId) continue;

    debugLog(`Processing import for unit ${leaseData.unitId}`, {
      importStartDate: leaseData.leaseStartDate,
      importEndDate: leaseData.leaseEndDate,
      importRent: leaseData.monthlyRent,
    });

    // Prefer the active lease (today within start/end) over a future one
    const unitLeases = existingLeases.filter((l) => l.unitId === leaseData.unitId);
    const now = dayjs();
    const existingLease =
      unitLeases.find((l) => {
        const start = toDate(l.leaseStartDate);
        const end = toDate(l.leaseEndDate);
        return start && end && !now.isBefore(dayjs(start), 'day') && !now.isAfter(dayjs(end), 'day');
      }) || unitLeases.find((l) => {
        const start = toDate(l.leaseStartDate);
        return start && now.isBefore(dayjs(start), 'day');
      }) || unitLeases[0] || null;

    debugLog(`Found existing lease for unit ${leaseData.unitId}`, {
      found: !!existingLease,
      existingLeaseId: existingLease?.id,
    });

    const preview = analyzeLeaseImport(leaseData, existingLease);

    debugLog(`Analysis result for unit ${leaseData.unitId}`, {
      action: preview.action,
      reason: preview.reason,
    });

    if (preview.action === 'skip') {
      toSkip.push(preview);
    } else {
      // Both 'create' and 'update' go to toCreate (TO PROCESS)
      toCreate.push(preview);
    }
  }

  const newLeases = toCreate.filter((p) => !p.existingLease).length;
  const updatedLeases = toCreate.filter((p) => p.existingLease).length;

  debugLog('generateLeaseImportPreview - Complete', {
    toCreateCount: toCreate.length,
    toSkipCount: toSkip.length,
    newLeases,
    updatedLeases,
  });

  return {
    toCreate,
    toSkip,
    summary: {
      totalImported: leaseUpdates.filter((l) => l.unitId).length,
      newLeases,
      updatedLeases,
      skipped: toSkip.length,
    },
  };
}
